<template>

  <div class="drawer md:drawer-open">
    <!-- Skip link at the very top -->
    <a href="#main-content" class="skip-link">
      Skip to main content
    </a>
    <input id="my-drawer-2" type="checkbox" class="drawer-toggle" v-model="drawerOpen" />
    <div v-show="!isFullScreen"
      class="drawer-content flex flex-col items-center justify-center">

      <!-- Page content here -->
      <main 
        class="w-full min-h-screen primary-content flex flex-col relative overflow-hidden"
      >

        <the-json-pretty :data="subscribedChannels.channels" title="Subscribed Channels" />

        <div class="flex justify-between items-center">

          <div class="flex-none md:hidden" v-if="!drawerOpen">
            <button @click="toggleDrawer" :aria-label="drawerOpen ? 'Close sidebar' : 'Open sidebar'"
              class="btn btn-square btn-ghost">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                class="inline-block w-6 h-6 stroke-current" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16">
                </path>
              </svg>
            </button>
          </div>

          <TheWebappTopNav class="mx-auto" />
        </div>

        <div class="w-full m-auto mt-0" style="min-height:70vh;">
          <NuxtPage :transition="false" />
        </div>
      </main>

      <TheFooter class="mt-auto mx-auto hidden md:grid" />

    </div>
    <div class="drawer-side">
      <label for="my-drawer-2" aria-label="close sidebar" class="drawer-overlay"></label>
      <div class="bg-base-200 h-full p-2 flex flex-col w-full"
        :style="{ width: drawerWidth }">

        <div class="flex justify-between items-center">

          <!-- logo -->
          <div class="max-w-sm navbar-start">
            <NuxtLink to="/" class="p-3 logo-dark hidden">
              <img src="@/assets/img/supapm-logo-dark.svg" alt="Logo" width="80px" height="33px">
            </NuxtLink>
            <NuxtLink to="/" class="p-3 logo-light hidden">
              <img src="@/assets/img/supapm-logo.svg" alt="Logo" width="80px" height="33px">
            </NuxtLink>
          </div>

          <div class="flex flex-row gap-2">

            <!-- button to close drawer -->
            <button v-if="showCloseDrawerButton" @click="toggleDrawer" class="btn btn-square btn-ghost md:hidden"
              :class="{ 'hidden': isFullScreen }">
              <img src="@/assets/icons/arrow-left.svg" alt="Logo" width="30px" height="30px" class="icon m-auto">
            </button>

            <!-- Button to go full screen -->
            <button v-if="showFullScreenButton" @click="toggleFullScreen"
              class="btn btn-square btn-ghost hidden md:block">
              <img src="@/assets/icons/clipboard-import.svg" alt="Logo" width="25px" height="25px"
                class="icon m-auto -rotate-90" :class="{ 'rotate-90': isFullScreen }">
            </button>

          </div>

        </div>

        <div class="mx-auto w-full" style="max-width: 500px;height:93%;">
          <AiChat :company_id="company?.id" @company-created="isFullScreen = false" @project-refreshed="handleProjectRefreshed" ref="aiChatRef" />
        </div>

      </div>
    </div>
    <!-- Button in bottom right corner of browser that opens the drawer. only visible on mobile -->
    <div class="fixed bottom-4 right-4 z-50 md:hidden" v-if="!isFullScreen && !drawerOpen">
      <button class="btn btn-primary btn-circle" @click="toggleDrawer">
        <img src="@/assets/img/supapm-logo-square-dark.svg" alt="Menu" width="25px" height="25px" class="m-auto"
          :class="{ 'hidden': drawerOpen }">
      </button>
    </div>
  </div>

</template>

<script setup>
import { useRoute } from 'vue-router'
import { useEventListener } from '@vueuse/core'
import { useAiChat } from '@/composables/useAiChat'

definePageMeta({
  middleware: ['auth', 'company']
})

const company = useCompanyData()
const drawerOpen = ref(false)
const isFullScreen = ref(false)
const aiChatRef = ref(null)
const mainContent = ref(null)
const { drawerShouldOpen, clearDrawerSignal } = useAiChat()
const subscribedChannels = useSubscribedChannels()

const route = useRoute()

// Provide project refresh event for pages to inject
const projectRefreshEvent = ref(null)
provide('projectRefreshEvent', projectRefreshEvent)

// Focus management for main content
const focusMainContent = () => {
  nextTick(() => {
    if (mainContent.value) {
      mainContent.value.focus()
    }
  })
}

// Watch route changes to handle onboarding and focus management
watch(() => route.path, async (newPath) => {
  if (newPath === '/onboard') {
    drawerOpen.value = true
  }
  
  // Focus main content on route change for accessibility
  await nextTick()
  focusMainContent()
}, { immediate: true })

// Watch for drawer signal
watch(drawerShouldOpen, (shouldOpen) => {
  if (shouldOpen) {
    drawerOpen.value = true
    // Clear the signal after we've handled it
    clearDrawerSignal()
    // Focus the chat input after drawer is open
    nextTick(() => {
      aiChatRef.value?.focusInput()
    })
  }
})

onMounted(async () => {
  console.log("webapp layout mounted")
})

const toggleDrawer = () => {
  // console.log("toggleDrawer", drawerOpen.value)
  drawerOpen.value = !drawerOpen.value
  
  // If we're opening the drawer, focus the input
  if (drawerOpen.value) {
    // Use nextTick to ensure the drawer is fully opened before focusing
    nextTick(() => {
      aiChatRef.value?.focusInput()
    })
  }
}

const drawerWidth = computed(() => {

  console.log("route.path", route.path)

  // we need logic to check if we're on the onboarding page
  if(isFullScreen.value) {
    return '100vw'
  }

  return '350px'
})

const toggleFullScreen = () => {
  console.log("toggleFullScreen")
  
  isFullScreen.value = !isFullScreen.value
}

const handleProjectRefreshed = (data) => {
  console.log('Project refresh requested:', data.project_id)
  projectRefreshEvent.value = data
  // Reset after a tick so watchers can detect the change
  nextTick(() => {
    projectRefreshEvent.value = null
  })
}

const showCloseDrawerButton = computed(() => {
  if(isFullScreen.value) {
    return false
  }

  if(route.path === '/onboard') {
    return false
  }

  return true
})

const showFullScreenButton = computed(() => {
  if(route.path === '/onboard') {
    return false
  }
  
  return true
})

// Keyboard shortcut to focus chat: Cmd+Shift+L / Ctrl+Shift+L
useEventListener('keydown', (event) => {
  // Check if Cmd+Shift+L (Mac) or Ctrl+Shift+L (Windows/Linux) is pressed
  const isCmdShiftL = (event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'l'
  
  if (isCmdShiftL && company.value) {
    // Don't trigger if user is typing in an input, textarea, select, or content editable element
    const activeElement = document.activeElement
    const tagName = activeElement?.tagName?.toLowerCase()
    
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || activeElement?.contentEditable === 'true') {
      return
    }
    
    // Prevent default browser behavior
    event.preventDefault()
    event.stopPropagation()
    
    // Open drawer if closed
    if (!drawerOpen.value) {
      drawerOpen.value = true
    }
    
    // Focus the chat input after ensuring drawer is open
    nextTick(() => {
      aiChatRef.value?.focusInput()
    })
  }
})

</script>